import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type {
  BulkUpsertResult,
  Currency,
  ListResponse,
  SalaryDebtSummary,
  SalaryEntry,
  SalaryPayment,
  SalaryReport,
  SalaryStatus,
} from './types'

const ROOT = '/salary-entries'
const KEY = ['salary-entries'] as const
const REPORT_KEY = ['reports', 'salaries'] as const

// Aggregate "what we still owe employees" across open accruals. Honors the same
// org filter as the entries list so the dashboard total matches a filtered view.
export function useSalaryDebtSummary(filter: { organizationId?: string | null } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'debt-summary', filter.organizationId ?? null],
    queryFn: () =>
      api<SalaryDebtSummary>(
        `${ROOT}/debt-summary${buildQuery({
          organization_id: filter.organizationId ?? undefined,
        })}`,
      ),
  })
}

export type SalaryEntriesFilters = {
  employeeId?: string | null
  organizationId?: string | null
  periodYear?: number | null
  periodMonth?: number | null
  status?: SalaryStatus | null
  paymentDateFrom?: string | null
  paymentDateTo?: string | null
}

function salaryEntriesKey(filters: SalaryEntriesFilters) {
  return {
    employeeId: filters.employeeId ?? null,
    organizationId: filters.organizationId ?? null,
    periodYear: filters.periodYear ?? null,
    periodMonth: filters.periodMonth ?? null,
    status: filters.status ?? null,
    paymentDateFrom: filters.paymentDateFrom ?? null,
    paymentDateTo: filters.paymentDateTo ?? null,
  }
}

export function useSalaryEntries(
  filters: SalaryEntriesFilters = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi()
  const key = salaryEntriesKey(filters)
  return useQuery({
    queryKey: [...KEY, key],
    enabled: options.enabled ?? true,
    queryFn: () =>
      api<ListResponse<SalaryEntry>>(
        `${ROOT}${buildQuery({
          employee_id: filters.employeeId ?? undefined,
          organization_id: filters.organizationId ?? undefined,
          period_year: filters.periodYear ?? undefined,
          period_month: filters.periodMonth ?? undefined,
          status: filters.status ?? undefined,
          payment_date_from: filters.paymentDateFrom ?? undefined,
          payment_date_to: filters.paymentDateTo ?? undefined,
        })}`,
      ),
  })
}

export function flattenSalaryEntries(
  data: ListResponse<SalaryEntry> | undefined,
): SalaryEntry[] {
  return data?.items ?? []
}

export type CreateSalaryEntryInput = {
  employeeId: string
  periodYear: number
  periodMonth: number
  gross: number
  taxes: number
  net: number
  currency: Currency
  paymentDate: string
  notes?: string | null
}

export function useCreateSalaryEntry() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateSalaryEntryInput) =>
      api<SalaryEntry>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: REPORT_KEY })
    },
  })
}

export type PatchSalaryEntryInput = {
  id: string
  patch: Partial<{
    periodYear: number
    periodMonth: number
    gross: number
    taxes: number
    net: number
    currency: Currency
    paymentDate: string
    notes: string | null
  }>
}

export function usePatchSalaryEntry() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: PatchSalaryEntryInput) =>
      api<SalaryEntry>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: REPORT_KEY })
    },
  })
}

export function useDeleteSalaryEntry() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: REPORT_KEY })
    },
  })
}

export type BulkUpsertSalariesInput = {
  periodYear: number
  periodMonth: number
  items: Array<{
    employeeId: string
    gross: number
    taxes: number
    net: number
    currency: Currency
    paymentDate: string
    notes?: string | null
  }>
}

export function useBulkUpsertSalaries() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BulkUpsertSalariesInput) =>
      api<BulkUpsertResult>(`${ROOT}/bulk-upsert`, { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: REPORT_KEY })
    },
  })
}

const PAYMENTS_KEY = ['salary-payments'] as const

// The payouts that settle a single accrual (1 accrual : N payouts).
export function useSalaryPayments(entryId: string | null, options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...PAYMENTS_KEY, entryId],
    enabled: (options.enabled ?? true) && Boolean(entryId),
    queryFn: () => api<ListResponse<SalaryPayment>>(`${ROOT}/${entryId}/payments`),
  })
}

// Side effects shared by recording and deleting a payout: the payout creates or
// removes an expense transaction, which moves balances, cashflow and ledgers.
function invalidateAfterPaymentChange(qc: ReturnType<typeof useQueryClient>, entryId: string) {
  qc.invalidateQueries({ queryKey: KEY })
  qc.invalidateQueries({ queryKey: [...PAYMENTS_KEY, entryId] })
  qc.invalidateQueries({ queryKey: REPORT_KEY })
  qc.invalidateQueries({ queryKey: ['account-balance'] })
  qc.invalidateQueries({ queryKey: ['reports', 'cashflow'] })
  qc.invalidateQueries({ queryKey: ['transactions'] })
  qc.invalidateQueries({ queryKey: ['account-movement'] })
}

export type AddSalaryPaymentInput = {
  id: string
  accountId: string
  amount: number
  paymentDate: string
}

export function useAddSalaryPayment() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, accountId, amount, paymentDate }: AddSalaryPaymentInput) =>
      api<SalaryEntry>(`${ROOT}/${id}/payments`, {
        method: 'POST',
        body: { accountId, amount, paymentDate },
      }),
    onSuccess: (_data, { id }) => invalidateAfterPaymentChange(qc, id),
  })
}

export function useDeleteSalaryPayment() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, paymentId }: { id: string; paymentId: string }) =>
      api<void>(`${ROOT}/${id}/payments/${paymentId}`, { method: 'DELETE' }),
    onSuccess: (_data, { id }) => invalidateAfterPaymentChange(qc, id),
  })
}

export type SalaryReportMetric = 'gross' | 'net' | 'paid' | 'debt'

export type SalaryReportFilters = {
  dateFrom: string
  dateTo: string
  metric?: SalaryReportMetric
  organizationId?: string | null
  employeeIds?: string[]
}

function salaryReportKey(filters: SalaryReportFilters) {
  return {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    metric: filters.metric ?? 'gross',
    organizationId: filters.organizationId ?? null,
    employeeIds: [...(filters.employeeIds ?? [])].sort(),
  }
}

export function useSalaryReport(
  filters: SalaryReportFilters,
  options: { enabled?: boolean } = {},
) {
  const api = useApi()
  const key = salaryReportKey(filters)
  return useQuery({
    queryKey: [...REPORT_KEY, key],
    enabled: options.enabled ?? true,
    queryFn: () => {
      const query = buildQuery({
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        metric: key.metric,
        organization_id: filters.organizationId ?? undefined,
        employee_id: key.employeeIds,
      })
      return api<SalaryReport>(`/reports/salaries${query}`)
    },
  })
}
